import { getAxiosAdminOrganizationRequest } from "@API";
import {
  createGetPendingUserUpdateRequestsInterceptors,
  createPutUserUpdateRequestActionInterceptors,
} from "@API/UserUpdateRequests/middlewares";
import {
  IAPIOrgUserUpdateRequest,
  IGetAxiosUserUpdateRequests,
  IPutAxiosUserUpdateRequestAction,
} from "@API/UserUpdateRequests/types";
import { parseDateStringToDate } from "@API/Utilities";
import {
  IOrgUserUpdateRequest,
  UserUpdateRequestId,
} from "@Store/UserUpdateRequests/types";
import { OrganizationId } from "@Store/Organizations/types";
import { AxiosInstance, AxiosResponse } from "axios";

export const PROCESSED_USER_UPDATE_REQUESTS_LIMIT = 100;

const parseOrgUserUpdateRequest = (
  apiRequest: IAPIOrgUserUpdateRequest
): IOrgUserUpdateRequest => ({
  ...apiRequest,
  requestTs: parseDateStringToDate(apiRequest.requestTs),
  requestValidUntil:
    apiRequest.requestValidUntil === null
      ? null
      : parseDateStringToDate(apiRequest.requestValidUntil),
  approvalTs:
    apiRequest.approvalTs === null
      ? null
      : parseDateStringToDate(apiRequest.approvalTs),
  declineTs:
    apiRequest.declineTs === null
      ? null
      : parseDateStringToDate(apiRequest.declineTs),
});

const sortByRequestTsDesc = (
  a: IOrgUserUpdateRequest,
  b: IOrgUserUpdateRequest
): number => b.requestTs.getTime() - a.requestTs.getTime();

export const getAxiosUserUpdateRequests = async (
  orgId: OrganizationId,
  params: { pending?: boolean; limit?: number } = {}
): Promise<IGetAxiosUserUpdateRequests> => {
  const request: AxiosInstance = getAxiosAdminOrganizationRequest();

  createGetPendingUserUpdateRequestsInterceptors(request);

  const query = new URLSearchParams();
  if (params.pending !== undefined) {
    query.set("pending", String(params.pending));
  }
  if (params.limit !== undefined) {
    query.set("limit", String(params.limit));
  }

  const queryString = query.toString();
  const response: AxiosResponse<IAPIOrgUserUpdateRequest[]> = await request.get(
    `${orgId}/userUpdateRequests${queryString ? `?${queryString}` : ""}`
  );

  const parsedData: IOrgUserUpdateRequest[] = response.data.map(
    parseOrgUserUpdateRequest
  );

  return { data: parsedData };
};

export const getAxiosMergedUserUpdateRequests = async (
  orgId: OrganizationId
): Promise<IGetAxiosUserUpdateRequests> => {
  const [pendingResponse, processedResponse] = await Promise.all([
    getAxiosUserUpdateRequests(orgId, { pending: true }),
    getAxiosUserUpdateRequests(orgId, {
      pending: false,
      limit: PROCESSED_USER_UPDATE_REQUESTS_LIMIT,
    }),
  ]);

  const processedSorted = [...processedResponse.data].sort(sortByRequestTsDesc);

  return {
    data: [...pendingResponse.data, ...processedSorted],
  };
};

export const putAxiosApproveUserUpdateRequest = async (
  orgId: OrganizationId,
  requestId: UserUpdateRequestId
): Promise<IPutAxiosUserUpdateRequestAction> => {
  const request: AxiosInstance = getAxiosAdminOrganizationRequest();

  createPutUserUpdateRequestActionInterceptors(request, "approve");

  await request.put(`${orgId}/userUpdateRequests/${requestId}/approve`);

  return {};
};

export const putAxiosDeclineUserUpdateRequest = async (
  orgId: OrganizationId,
  requestId: UserUpdateRequestId
): Promise<IPutAxiosUserUpdateRequestAction> => {
  const request: AxiosInstance = getAxiosAdminOrganizationRequest();

  createPutUserUpdateRequestActionInterceptors(request, "decline");

  await request.put(`${orgId}/userUpdateRequests/${requestId}/decline`);

  return {};
};
